import { IStressResponse } from '@toro/common/shared/interfaces/pos-risk/stress.interface';
import { Component, OnInit, EventEmitter, Output, Input, ViewEncapsulation, OnDestroy, ChangeDetectorRef } from '@angular/core';
import { OrderStatusEnum } from '@toro/common/shared/enum/orders/status.enum';
import { OrdersService } from '@toro/common/shared/services/orders/orders.service';
import { RejectionReasonService, RejectionReason } from '../../shared/services/rejection-reason/rejection-reason.service'
import { OrderSideEnum } from '@toro/common/shared/enum/orders/side.enum';
import { ComputerService } from '@toro/common/shared/services/computer/computer.service';
import { Store } from '@ngrx/store';
import { AppState } from '@toro/common/shared/interfaces/app-state.interface';
import { Subscription } from 'rxjs';
import { OrderType } from '@toro/common/shared/enum/orders/order-type.enum';
import { OrderMarketTypeEnum } from '@toro/common/shared/enum/orders/market-type.enum';
import { KeyStocks } from '@toro/common/shared/enum/key-stocks/key-stocks.enum';
import { ActiveQuote } from '@toro/common/shared/models/realtime/active-quote.model';
import { delay, first, map } from 'rxjs/operators';
import { AutoUnsubscribe } from 'ngx-auto-unsubscribe';

@AutoUnsubscribe()
@Component({
    selector: 'hub-history',
    templateUrl: './hub-history.component.html',
    styleUrls: ['./hub-history.component.scss'],
    encapsulation: ViewEncapsulation.None,
})
export class HubHistoryComponent implements OnInit, OnDestroy {
    @Input() public historyDetails: any;
    @Output() public emmiter: EventEmitter<boolean> = new EventEmitter();
    @Input() public symbol: string;
    @Input() public clickedDate: Date;

    public active: boolean = false;
    public typeOrder: string = '';
    public orderMarketType: string = '';
    public rejectionReason: RejectionReason;
    public enumStatus: any;
    public enumSide: any;
    public historyStatusDetails: any;
    public typeOrderSide: boolean;
    public result: number;
    public isPtax: any;
    public averagePrice: number;
    public guaranteeUsed: number;
    public isBuy: boolean;
    public averagePriceComputed: boolean;
    private instrumentSubscription: Subscription;
    private stressSubscription: Subscription;
    private dolPtaxQuote: number;
    private dolPtaxQuoteSubscription: Subscription;
    public quoteFactor: number;
    public isFuture: boolean;
    public instrumet: any;
    public taxBrokerage = 0.1;
    public gainTaxFactor = 1.039;
    

    constructor(
        private orderService: OrdersService,
        private computeService: ComputerService,
        private rejectionReasonService: RejectionReasonService,
        private store: Store<AppState>,
        private changeDetector: ChangeDetectorRef,
    ) {
        this.enumStatus = OrderStatusEnum;
        this.enumSide = OrderSideEnum;
    }

    public isMobile(): boolean {
        return window.innerWidth <= 1439;
    }

    public calculatePrice(totalExecutedValue: number, cumQty: number) {
        return this.computeService.computeMediumPrice(totalExecutedValue, cumQty);
    }

    // Animating cards
    public toggleCard() {
        if (this.isMobile()) {
            this.active = !this.active;
        }
    }

    public ngOnInit() {
        this.historyStatusDetails = this.orderService.translateOrderStatus(this.historyDetails);
        this.rejectionReason = this.rejectionReasonService.getRejectionReason(this.historyDetails.text);
    }

    public ngAfterViewInit() {
        
        if ( this.historyDetails.orderMarketType === 0 ) {
            this.orderMarketType = 'Ação';
        } else if ( this.historyDetails.orderMarketType === 1) {
            this.orderMarketType = 'Futuro';
            this.isFuture = true;
        } else if ( this.historyDetails.orderMarketType === 2) {
            this.orderMarketType = 'Opção';
        }
        // preço médio
        this.averagePrice = this.historyDetails.avaragePriceExecuted;
        this.averagePriceComputed = !isNaN(this.averagePrice);

        this.guaranteeUsed = this.historyDetails.guaranteeUsed;

        if (this.historyDetails.strategyID) {
            const inputLeg = this.historyDetails.legs.find((leg) => leg.internalOrderType === OrderType.Input);
            this.isBuy = inputLeg && inputLeg.side === OrderSideEnum.Compra;
        } else {
            this.isBuy = this.historyDetails.side === OrderSideEnum.Compra;
        }

        if (this.historyDetails.closed && this.historyDetails.strategyID) {
            // cotação dol ptax
            this.dolPtaxQuoteSubscription = this.store
                .select((stocksExchangeStore) => stocksExchangeStore.stocksExchange.stocksMarket.quote)
                .pipe(
                    map((quote) => quote[KeyStocks.DolPtax]),
                    first((index) => index),
                    delay(10)// evitar erro de detect changes,
                )
                .subscribe( (quote: ActiveQuote) => {
                    this.dolPtaxQuote = quote.PriceLastTrade || quote.OpeningPrice || quote.ClosingPricePreviousDay;
                    this.computeResult();
                });

            // instrumento
            this.instrumentSubscription = this.store
                .select((stocksExchangeStore) => stocksExchangeStore.stocksExchange.stocksMarket.instruments)
                .pipe(
                    map((instruments) => instruments[this.symbol]),
                    first((instrument) => instrument ? true : false),
                    delay(10)// evitar erro de detect changes,
                )
                .subscribe((instrument) => {
                    this.isFuture = instrument.typeMarket === OrderMarketTypeEnum.Future;
                    this.quoteFactor = instrument.quoteFactor;
                    this.instrumet = instrument;
                    this.isPtax = (instrument.stress as IStressResponse).isPtax;
                    this.computeResult();
                });
        }

        this.changeDetector.detectChanges();
    }

    public ngOnDestroy() {}

    /** Calcula a taxa provisionada */
    public provisionedBrokerage(value: number): number {
        if (value > 0) {
            value -= (value * this.taxBrokerage) * this.gainTaxFactor;
        }
        return value;
    }

    /** Calcula o resultado para uma estratégia encerrada */
    public computeResult() {
        if (this.instrumet === undefined || this.isPtax === undefined || (this.isPtax && this.dolPtaxQuote === undefined)) {
            return;
        }

        const inputLeg = this.historyDetails.legs.find((leg) => leg.internalOrderType === OrderType.Input);
        const lossStopLeg = this.historyDetails.legs.find((leg) => leg.internalOrderType === OrderType.StopLoss);
        const gainStopLeg = this.historyDetails.legs.find((leg) => leg.internalOrderType === OrderType.StopGain);
        const executedLeg = gainStopLeg.avaragePriceExecuted ? gainStopLeg : lossStopLeg;
        
        this.typeOrderSide = this.isCalendarExecutionDate(executedLeg.executionDate);
        if(!this.typeOrderSide) return;

        this.result = this.computeService.ComputeResultValue(
            inputLeg.orderQty,
            this.quoteFactor,
            executedLeg.avaragePriceExecuted,
            inputLeg.avaragePriceExecuted,
            this.isFuture,
            this.isBuy,
            (this.isPtax) ? this.dolPtaxQuote : null);

        this.result = this.provisionedBrokerage(this.result);
    }

    /**
     * Method that verifies if the calendar date is equals to executedLeg execution Date;
     * If yes, we show the result value, else, we hide the result.
     * @param executionDate 
     */
    public isCalendarExecutionDate(executionDate: string): boolean {
        executionDate = new Date(executionDate).toLocaleDateString();
        return executionDate === this.clickedDate.toLocaleDateString();
    }

}
